import type { RefObject } from 'react';
import { useCallback, useMemo } from 'react';

import { chartThemeTokens } from 'design-system';
import type { ChartEventsOptions, PlotOptions, XAxisOptions } from 'highcharts';
import { dateFormat } from 'highcharts';

import type Highcharts from 'highcharts/highstock';
import watermarkBlackJpg from '@/assets/watermarks/watermark-black.jpg';
import watermarkBlackRaw from '@/assets/watermarks/watermark-black.svg?raw';
import watermarkColorJpg from '@/assets/watermarks/watermark-color.jpg';
import watermarkColorRaw from '@/assets/watermarks/watermark-color.svg?raw';
import { Z_INDEX } from '@/config/z-index';
import { useUserConfig } from '@/features/_shared/user';
import {
  createChartAppearance,
  type ChartAppearance
} from '@/features/charts/theme/chartAppearance';
import type { WatermarkAttributes } from '@/features/charts/types/highcharts';

function sliceConfigFormat(firstOccurrence: number, configFormat: string, lastOccurrence: number) {
  if (firstOccurrence === 0) {
    return configFormat.slice(lastOccurrence + 2, configFormat.length);
  } else if (lastOccurrence === configFormat.length - 1) {
    return configFormat.slice(0, firstOccurrence - 1);
  }
  return configFormat;
}

// Vite serves image assets as URL paths in dev mode, but the Highcharts online export
// server can't reach local URLs. Pre-fetch and cache JPGs as data URIs at module load
// so they're ready when exportEvents.load() fires.
const toDataUri = (src: string): Promise<string> => {
  if (src.startsWith('data:')) return Promise.resolve(src);
  return fetch(src)
    .then(r => r.blob())
    .then(
      blob =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve(reader.result as string);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );
};

const jpgDataUriCache: Record<string, string | undefined> = {};
let jpgDataUriPrefetchPromise: Promise<void> | undefined;

export const prefetchWatermarkJpgDataUris = async (): Promise<void> => {
  const [blackResult, colorResult] = await Promise.allSettled([
    toDataUri(watermarkBlackJpg),
    toDataUri(watermarkColorJpg)
  ]);
  if (blackResult.status === 'fulfilled') {
    jpgDataUriCache['/watermark-black.svg'] = blackResult.value;
  }
  if (colorResult.status === 'fulfilled') {
    jpgDataUriCache['/watermark-color.svg'] = colorResult.value;
  }
};

export const ensureWatermarkJpgDataUris = (): Promise<void> => {
  jpgDataUriPrefetchPromise ??= prefetchWatermarkJpgDataUris();
  return jpgDataUriPrefetchPromise;
};

// Avoid blocking module import in test environments where asset fetches can hang.
if (import.meta.env.MODE !== 'test' && !import.meta.env.VITEST) {
  ensureWatermarkJpgDataUris();
}

export const CHART_GRID_COLOR = chartThemeTokens.dark.grid;

export interface ChartDownloadProperties {
  format: string;
  fullScreen?: boolean;
  darkMode?: boolean;
  chartLocation: ChartParentComponent | null;
}

export interface ChartParentComponent {
  mainPageTitle: string;
  subPageTitle?: string;
  tabName?: string;
}

export interface ExtendedPlotOptions extends PlotOptions {
  custom?: object;
}

type WatermarkImageRef = RefObject<Highcharts.SVGElement | null>;
type WatermarkImageRefAccessor = WatermarkImageRef | (() => WatermarkImageRef);
const createYAxisOptions = (appearance: ChartAppearance) => ({
  title: { text: undefined },
  maxPadding: 0.1,
  offset: 0,
  opposite: true,
  labels: {
    align: 'left' as const,
    x: 12,
    y: 2,
    style: {
      color: appearance.foreground,
      fontSize: '10px'
    },
    formatter(): string {
      // @ts-expect-error: highcharts rules.
      return Number.parseFloat(this.value.toFixed(2)).toString();
    }
  },
  gridLineColor: appearance.grid
});

interface WatermarkConfig {
  dataUri: string;
  aspectRatio: number;
  svgContent: string;
  viewBox: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

// Match the JPG watermark resolution so rasterizers downsample (sharp) instead
// of upsample (blurry) when the SVG is rendered at chart plot width during export.
const WATERMARK_SVG_RENDER_WIDTH = 800;

const parseSvgNumber = (value: string | undefined): number | undefined => {
  if (!value) return undefined;
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? number : undefined;
};

const extractWatermarkConfig = (svgContent: string): WatermarkConfig => {
  const widthMatch = /width="([^"]+)"/.exec(svgContent);
  const heightMatch = /height="([^"]+)"/.exec(svgContent);
  const viewBoxMatch = /viewBox="([^"]+)"/.exec(svgContent);
  const [viewBoxX = 0, viewBoxY = 0, viewBoxWidth = 1, viewBoxHeight = 1] =
    viewBoxMatch?.[1]
      .split(/[\s,]+/)
      .map(Number)
      .filter(Number.isFinite) ?? [];
  const w = parseSvgNumber(widthMatch?.[1]) ?? viewBoxWidth;
  const h = parseSvgNumber(heightMatch?.[1]) ?? viewBoxHeight;
  const aspectRatio = w / h;

  const scaledH = Math.round(WATERMARK_SVG_RENDER_WIDTH / aspectRatio);
  const hiResSvg = svgContent
    .replace(`width="${w}"`, `width="${WATERMARK_SVG_RENDER_WIDTH}"`)
    .replace(`height="${h}"`, `height="${scaledH}"`);

  return {
    dataUri: `data:image/svg+xml;base64,${btoa(hiResSvg)}`,
    aspectRatio,
    svgContent,
    viewBox: {
      x: viewBoxX,
      y: viewBoxY,
      width: viewBoxWidth,
      height: viewBoxHeight
    }
  };
};

const watermarkConfigs: Record<string, WatermarkConfig> = {
  '/watermark-black.svg': extractWatermarkConfig(watermarkBlackRaw),
  '/watermark-color.svg': extractWatermarkConfig(watermarkColorRaw)
};

const defaultWatermarkConfig: WatermarkConfig = {
  dataUri: '',
  aspectRatio: 1,
  svgContent: '',
  viewBox: {
    x: 0,
    y: 0,
    width: 1,
    height: 1
  }
};

export const getWatermarkConfig = (name: string): WatermarkConfig =>
  watermarkConfigs[name] ?? defaultWatermarkConfig;

export const getStandardWatermarkAttributes = ({
  chart,
  aspectRatio,
  useSmallWatermark
}: {
  chart: Highcharts.Chart;
  aspectRatio: number;
  useSmallWatermark: boolean;
}): WatermarkAttributes => {
  const plotWidth = chart.plotWidth;
  const isRestricted = !useSmallWatermark;
  const w = isRestricted ? Math.round(plotWidth) : 100;
  const h = Math.round(w / aspectRatio);
  return isRestricted
    ? {
        width: w,
        height: h,
        x: -w / 2,
        y: -h / 2,
        zIndex: Z_INDEX.base,
        align: 'center',
        verticalAlign: 'middle'
      }
    : {
        width: w,
        height: h,
        x: -w,
        y: -h + 12,
        zIndex: Z_INDEX.base,
        align: 'right',
        verticalAlign: 'bottom'
      };
};

export const mergeChartEvents = (
  baseEvents: ChartEventsOptions | undefined,
  nextEvents: ChartEventsOptions
): ChartEventsOptions => {
  if (!baseEvents) return nextEvents;
  const merged: ChartEventsOptions = { ...baseEvents };
  const mergedEvents = merged as Record<
    string,
    ChartEventsOptions[keyof ChartEventsOptions] | undefined
  >;

  for (const eventName of Object.keys(nextEvents) as Array<keyof ChartEventsOptions>) {
    const nextHandler = nextEvents[eventName];
    if (!nextHandler) continue;
    const baseHandler = baseEvents[eventName];
    if (baseHandler) {
      const mergedHandler = function (this: Highcharts.Chart, ...args: unknown[]) {
        (baseHandler as (...args: unknown[]) => void).apply(this, args);
        (nextHandler as (...args: unknown[]) => void).apply(this, args);
      };
      mergedEvents[eventName] = mergedHandler;
    } else {
      mergedEvents[eventName] = nextHandler;
    }
  }

  return merged;
};

const getAlignFactor = (
  align: WatermarkAttributes['align'] | WatermarkAttributes['verticalAlign']
) => {
  if (align === 'center' || align === 'middle') return 0.5;
  if (align === 'right' || align === 'bottom') return 1;
  return 0;
};

const getAlignedWatermarkPosition = (
  chart: Highcharts.Chart,
  attrs: WatermarkAttributes
): { x: number; y: number } => {
  const rendererWithPlotBox: Highcharts.SVGRenderer & {
    plotBox?: { x?: number; y?: number; width?: number; height?: number };
  } = chart.renderer;
  const alignTo = rendererWithPlotBox.plotBox ?? {
    x: chart.plotLeft,
    y: chart.plotTop,
    width: chart.plotWidth,
    height: chart.plotHeight
  };

  return {
    x:
      (alignTo.x ?? 0) +
      attrs.x +
      ((alignTo.width ?? 0) - attrs.width) * getAlignFactor(attrs.align),
    y:
      (alignTo.y ?? 0) +
      attrs.y +
      ((alignTo.height ?? 0) - attrs.height) * getAlignFactor(attrs.verticalAlign)
  };
};

const createInlineExportWatermark = (
  chart: Highcharts.Chart,
  watermarkImageName: string,
  attrs: WatermarkAttributes
): Highcharts.SVGElement | null => {
  const watermark = getWatermarkConfig(watermarkImageName);
  if (!watermark.svgContent) return null;

  const parsedSvg = new DOMParser().parseFromString(
    watermark.svgContent,
    'image/svg+xml'
  ).documentElement;
  if (parsedSvg.nodeName.toLowerCase() === 'parsererror') return null;

  chart.container
    ?.querySelectorAll('.highcharts-gx-export-watermark')
    .forEach(node => node.remove());

  const group = chart.renderer.g('gx-export-watermark').attr({ zIndex: attrs.zIndex }).add();
  const target = group.element;
  const doc = target.ownerDocument;
  Array.from(parsedSvg.childNodes).forEach(child => {
    target.appendChild(doc.importNode(child, true));
  });

  const scaleX = attrs.width / watermark.viewBox.width;
  const scaleY = attrs.height / watermark.viewBox.height;
  const { x, y } = getAlignedWatermarkPosition(chart, attrs);
  target.setAttribute(
    'transform',
    `translate(${x - watermark.viewBox.x * scaleX} ${y - watermark.viewBox.y * scaleY}) scale(${scaleX} ${scaleY})`
  );
  target.setAttribute('pointer-events', 'none');

  return group;
};

export const createEventOptions = (
  watermarkImage: WatermarkImageRefAccessor,
  getWatermarkAttributes: (chart: Highcharts.Chart, isExport?: boolean) => WatermarkAttributes,
  watermarkImageName: string
): { events: ChartEventsOptions; exportEvents: ChartEventsOptions } => {
  const { dataUri } = getWatermarkConfig(watermarkImageName);
  const resolveWatermarkRef = (): WatermarkImageRef =>
    typeof watermarkImage === 'function' ? watermarkImage() : watermarkImage;

  const applyWatermarkLayout = (image: Highcharts.SVGElement, attrs: WatermarkAttributes) => {
    image.attr({
      width: attrs.width,
      height: attrs.height,
      zIndex: attrs.zIndex
    });
    image.align(
      {
        align: attrs.align ?? 'right',
        verticalAlign: attrs.verticalAlign ?? 'bottom',
        x: attrs.x,
        y: attrs.y
      },
      false,
      'plotBox'
    );
  };

  return {
    events: {
      load() {
        const attrs = getWatermarkAttributes(this);
        const watermarkImageRef = resolveWatermarkRef();
        watermarkImageRef.current = this.renderer.image(dataUri).add();
        applyWatermarkLayout(watermarkImageRef.current, attrs);
      },
      redraw() {
        const watermarkImageRef = resolveWatermarkRef();
        if (watermarkImageRef.current) {
          const attrs = getWatermarkAttributes(this);
          applyWatermarkLayout(watermarkImageRef.current, attrs);
        }
      }
    },
    exportEvents: {
      load() {
        this.setSize(null, this.chartHeight);

        const attrs = getWatermarkAttributes(this, true);

        const inlineWatermark = createInlineExportWatermark(this, watermarkImageName, attrs);
        if (!inlineWatermark) {
          const exportDataUri =
            this.options.exporting?.type === 'image/svg+xml'
              ? dataUri
              : (jpgDataUriCache[watermarkImageName] ?? dataUri);

          const image = this.renderer.image(exportDataUri).add();
          applyWatermarkLayout(image, attrs);
        }

        for (const series of this.series) {
          if (series.visible) continue;
          // @ts-expect-error: highcharts rules.
          series.update({ showInLegend: false });
        }
      }
    }
  };
};

const defaultAppearance = createChartAppearance(chartThemeTokens, 'regular');

export const useChartFunctions = (
  appearance: ChartAppearance = defaultAppearance,
  prefersReducedMotion = false
) => {
  const { data: userConfigData } = useUserConfig();

  const userDateFormat = useMemo(() => {
    let configFormat = userConfigData?.dateFormat.value ?? 'dd-MMM-yy';
    const firstOccurrence = configFormat.indexOf('d');
    const lastOccurrence = configFormat.lastIndexOf('d');

    configFormat = sliceConfigFormat(firstOccurrence, configFormat, lastOccurrence);
    const delimiter = configFormat.includes('-') ? '-' : '/';
    const configParts = configFormat.split(delimiter);

    configFormat = configParts
      .map((config: string) => {
        switch (config.toLowerCase()) {
          case 'mmm': {
            return '%b';
          }
          case 'mm': {
            return '%m';
          }
          case 'yy': {
            return '%y';
          }
          case 'yyyy': {
            return '%Y';
          }
          default:
            return null;
        }
      })
      .filter(Boolean)
      .join(delimiter);
    return configFormat;
  }, [userConfigData]);

  const createNavigator = useCallback(() => {
    const series: Highcharts.SeriesAreasplineOptions | undefined = prefersReducedMotion
      ? { type: 'areaspline', animation: false }
      : undefined;

    return {
      backgroundColor: appearance.navigatorBackground,
      xAxis: {
        gridLineColor: appearance.border,
        labels: {
          style: {
            color: appearance.foreground
          },
          formatter(): string {
            // @ts-expect-error: highcharts rules.
            const value = this.value;
            return dateFormat(userDateFormat, value);
          }
        }
      },
      enabled: true,
      adaptToUpdatedData: false,
      scrollbar: {
        enabled: true,
        barBackgroundColor: appearance.navigatorBackground,
        barBorderColor: appearance.border,
        buttonBackgroundColor: appearance.navigatorBackground,
        buttonBorderColor: appearance.border,
        buttonArrowColor: appearance.foreground,
        rifleColor: appearance.foreground,
        trackBackgroundColor: appearance.navigatorBackground,
        trackBorderColor: appearance.border
      },
      outlineColor: appearance.border,
      handles: {
        backgroundColor: appearance.navigatorBackground,
        borderColor: appearance.border
      },
      series
    };
  }, [appearance, prefersReducedMotion, userDateFormat]);

  const createPlotOptions = useCallback(
    (): ExtendedPlotOptions => ({
      series: {
        animation: false,
        states: {
          inactive: { opacity: 1 },
          hover: { enabled: false }
        }
      },
      line: {
        states: {
          hover: { lineWidth: 3 }
        }
      }
    }),
    []
  );

  const createXAxis = useCallback((): XAxisOptions => {
    return {
      tickInterval: 30 * 24 * 3600 * 1000,
      ordinal: false,
      labels: {
        formatter(): string {
          const value = Number(this.value);
          return dateFormat(userDateFormat, value);
        },
        style: {
          color: appearance.foreground,
          fontSize: '10px'
        }
      },
      gridLineColor: appearance.grid,
      lineColor: appearance.grid,
      tickColor: appearance.grid
    };
  }, [appearance.foreground, appearance.grid, userDateFormat]);

  const createYAxis = useCallback(() => createYAxisOptions(appearance), [appearance]);

  return {
    createNavigator,
    createPlotOptions,
    createYAxis,
    createXAxis,
    createEventOptions
  };
};

export default useChartFunctions;
